import type { Graphics } from 'pixi.js';
import { DEG2RAD } from '@softgames/math-utils';
import type { GraphicsModel } from './types/graphics';
import { GraphicsCommandType } from './types/graphics';

export const copyGraphicsProps = (graphics: Graphics, model: GraphicsModel): void => {
  graphics.x = model.x;
  graphics.y = model.y;
  graphics.scale.x = model.scale.x;
  graphics.scale.y = model.scale.y;
  graphics.pivot.x = model.pivot.x;
  graphics.pivot.y = model.pivot.y;
  graphics.angle = model.angle;
  graphics.alpha = model.alpha;
  graphics.visible = model.visible;
  graphics.tint = model.tint;
  graphics.blendMode = model.blendMode;
  graphics.interactive = model.interactive;
  graphics.eventMode = model.buttonMode ? 'static' : 'auto';
  graphics.cursor = model.buttonMode ? 'pointer' : 'default';

  if (model.commands) {
    // eslint-disable-next-line no-restricted-syntax
    for (const command of model.commands) {
      switch (command.type) {
        case GraphicsCommandType.BeginFill:
          graphics.fill(command.color);
          break;

        case GraphicsCommandType.LineStyle:
          graphics.setStrokeStyle({
            width: command.width,
            color: command.color,
            alpha: command.alpha,
          });
          break;

        case GraphicsCommandType.DrawRect:
          graphics.rect(command.x, command.y, command.width, command.height);
          break;

        case GraphicsCommandType.DrawRoundedRect:
          graphics.roundRect(command.x, command.y, command.width, command.height, command.radius);
          break;

        case GraphicsCommandType.DrawCircle:
          graphics.circle(command.x, command.y, command.radius);
          break;

        case GraphicsCommandType.DrawEllipse:
          graphics.ellipse(command.x, command.y, command.width, command.height);
          break;

        case GraphicsCommandType.DrawStar:
          graphics.star(
            command.x,
            command.y,
            command.points,
            command.radius,
            command.innerRadius,
            DEG2RAD * command.rotation,
          );
          break;

        case GraphicsCommandType.DrawPolygon:
          graphics.poly(command.path.split(/\s*,\s*/).map(Number));
          break;

        case GraphicsCommandType.MoveTo:
          graphics.moveTo(command.x, command.y);
          break;

        case GraphicsCommandType.LineTo:
          graphics.lineTo(command.x, command.y);
          break;

        case GraphicsCommandType.Arc:
          graphics.arc(
            command.cx,
            command.cy,
            command.radius,
            DEG2RAD * command.startAngle,
            DEG2RAD * command.endAngle,
            command.anticlockwise,
          );
          break;

        case GraphicsCommandType.ArcTo:
          graphics.arcTo(command.x1, command.y1, command.x2, command.y2, command.radius);
          break;

        case GraphicsCommandType.QuadraticCurveTo:
          graphics.quadraticCurveTo(command.cpX, command.cpY, command.toX, command.toY);
          break;

        case GraphicsCommandType.BezierCurveTo:
          graphics.bezierCurveTo(command.cpX, command.cpY, command.cpX2, command.cpY2, command.toX, command.toY);
          break;

        case GraphicsCommandType.BeginHole:
          console.warn('Graphics hole is not supported by PIXI 4');
          // graphics.beginHole();
          break;

        case GraphicsCommandType.EndHole:
          // graphics.endHole();
          break;

        case GraphicsCommandType.ClosePath:
          graphics.closePath();
          break;

        default:
          break;
      }
    }
  }
};
